import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import type { Artifact } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { useCallback, useState } from 'react'
import useSWR from 'swr'

import { artifactUrl, fetchArtifact } from './artifactApi'
import { putArtifact, useStoredArtifact } from './artifactStore'
import { downloadArtifact } from './downloadArtifact'

export interface UseArtifactResult {
  artifact: Artifact | undefined
  isLoading: boolean
  error: Error | undefined
  isDownloading: boolean
  download: () => Promise<Artifact>
}

/**
 * The artifact from the session store, revalidated by id on every mount; a
 * fetched copy only replaces the stored one when its revision is newer.
 * `download` renders and records the file through the kind registry.
 */
export function useArtifact(
  artifactId: string | undefined,
  siteId: string | undefined
): UseArtifactResult {
  const { authorizedAxios, isAuthenticated } = useGeckoAuth()
  const { POWER_BACKEND_AI_URL } = useRuntimeVariables()
  const stored = useStoredArtifact(artifactId)
  const url =
    artifactId && siteId
      ? artifactUrl(POWER_BACKEND_AI_URL, artifactId, siteId)
      : undefined
  const { data, error, isLoading } = useSWR<Artifact, Error>(
    isAuthenticated && url ? url : null,
    (key: string) => fetchArtifact(authorizedAxios, key),
    {
      revalidateOnMount: true,
      revalidateOnFocus: false,
      onSuccess: (fetched) => {
        putArtifact(fetched)
      },
    }
  )
  const [isDownloading, setIsDownloading] = useState(false)
  const artifact = stored ?? data

  const download = useCallback(async () => {
    if (!artifact) throw new Error(t`No document is loaded.`)
    setIsDownloading(true)
    try {
      return await downloadArtifact({
        artifact,
        axios: authorizedAxios,
        powerBackendUrl: POWER_BACKEND_AI_URL,
      })
    } finally {
      setIsDownloading(false)
    }
  }, [artifact, authorizedAxios, POWER_BACKEND_AI_URL])

  return {
    artifact,
    isLoading: artifact === undefined && (isLoading || !isAuthenticated),
    error: artifact === undefined ? error : undefined,
    isDownloading,
    download,
  }
}
